import React, {useState, useMemo} from "react";
import {Box, Paper, Stack, Typography, Chip, Button, Link, Tooltip} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {SeverityDot, StatusChip, HelpPopover, Identity} from "./cells";
import MiniBars from "./MiniBars";
import {
    fmtMoneyShort, reasonOf, earningMean, STATUS_WORD, STATUS_LEGEND, STATUS_META, severityOf, EMPTY,
    sinceKey, providerCoversNetwork, shortProvider, usualLabel, fmtDay, fmtCompact, isNilValue, usualOf,
} from "./format";
import dayjs from "dayjs";
import {TYPE, TABULAR, NEUTRAL, MONO, SEV, ATTENTION_ROW_H} from "./tokens";

const VISIBLE = 6;
const CHIP_ORDER = ["DEAD", "NO_ADS", "STOPPED", "WATCH", "FILTERED"];
const ALSO_ORDER = ["PARTIAL", "NO_TIGER", "NO_REVENUE", "NO_TRAFFIC"];
const GROUP_MIN = 3;
const GROUP_TAGS_SHOWN = 6;

export const ATTENTION_HELP = [
    ...CHIP_ORDER.map((s) => `${STATUS_META[s].label} — ${STATUS_META[s].hint}`),
    "Sorted by money at risk. 'usually $336/d → $0' = usual earning day vs last day.",
];

const barsWidthFor = (days) => (days > 14 ? 90 : 56); // 30 bars at 56 px would be 1 px each

/**
 * `usually $336/d → $0` — neutral: the dot and the chip are the only colours on the row. "Usually" = the API's 7-day median
 * (`yesterday.usual_rev`, with `(n d)` when the base is thin), else the mean of the earning days, else the previous range's per-day.
 */
const Money = ({mean, revPrev, days, last, y, sx}) => {
    const avgLabel = y && !isNilValue(y.usual_rev) ? usualLabel(y) : mean !== null ? `usually ${fmtMoneyShort(mean)}/d`
        : revPrev > 0 && days ? `usually ${fmtMoneyShort(revPrev / days)}/d` : `usually ${EMPTY}`;
    return (
        <Typography component="span" sx={{fontSize: 12, ...TABULAR, whiteSpace: "nowrap", color: "text.secondary", textAlign: "right", ...sx}}>
            {avgLabel} → <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>{fmtMoneyShort(last)}</Box>
        </Typography>
    );
};

const rowSx = (barsWidth, {selected, child, group}) => ({
    display: "grid", gridTemplateColumns: `12px 44px 74px minmax(0,1fr) auto ${barsWidth + 4}px 16px`, gridTemplateRows: "20px 18px",
    alignItems: "center", columnGap: 1, height: ATTENTION_ROW_H, px: 1.5, pl: child ? 3.5 : 1.5, cursor: "pointer", boxSizing: "border-box",
    borderBottom: `1px solid ${NEUTRAL.hairline}`,
    bgcolor: selected ? NEUTRAL.selected : group ? "rgba(0,0,0,.015)" : "transparent",
    "&:hover": {bgcolor: NEUTRAL.hover},
    "&:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing},
});

const keyActivate = (fn) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } };

/**
 * `sent 0 on Sep 13 (11k on Sep 12)` — when the partner has not reported the day but Tiger already shows the stop, the
 * reader must not read the alarm as "just a late pull". Null when the partner has reported.
 */
export const pendingNote = (row) => {
    const y = row && row.yesterday;
    if (!y || !y.provider_pending) return null;
    const prev = y.day ? fmtDay(dayjs.utc(y.day).subtract(1, "day").format("YYYY-MM-DD")) : "the day before";
    if ((Number(y.fs) || 0) === 0 && (Number(y.fs_d2) || 0) > 0) return `sent 0 on ${fmtDay(y.day)} (${fmtCompact(y.fs_d2)} on ${prev}) — a stop, not a late pull`;
    return `partner has not reported ${fmtDay(y.day)} yet`;
};

/** One tag, two lines: dot · tag · chip · site · partner / reason · money; bars span both lines. `launch` = API launch entry (adds `launch · day N`). */
export const AttentionRow = ({row, days, selected, onSelect, child = false, isNew = false, launch = null}) => {
    const barsWidth = barsWidthFor(days);
    const note = pendingNote(row);
    const reason = `${launch ? `launch · day ${launch.day_n} · ` : ""}${reasonOf(row)}${note ? ` · ${note}` : ""}`;
    const label = STATUS_META[row.status] ? STATUS_META[row.status].label : row.status;
    return (
        <Box role="button" tabIndex={0} aria-label={`${row.tag} ${label}${isNew ? ", new" : ""}, open details`}
             onClick={() => onSelect(row.tag)} onKeyDown={keyActivate(() => onSelect(row.tag))}
             sx={rowSx(barsWidth, {selected, child})}>
            <Box sx={{gridRow: 1, gridColumn: 1, display: "flex", alignItems: "center"}}>{!child && <SeverityDot status={row.status}/>}</Box>
            <Typography component="span" sx={{...TYPE.tag, gridRow: 1, gridColumn: 2}}>{row.tag}</Typography>
            <Box sx={{gridRow: 1, gridColumn: 3, display: "flex", alignItems: "center", gap: 0.5}}>
                {!child && <StatusChip status={row.status} sx={note ? {"&::after": {content: '"?"', ml: "1px"}} : undefined}/>}
            </Box>
            <Identity site={row.site} provider={row.provider} network={row.type_label} prefix={row.prefix} sx={{gridRow: 1, gridColumn: "4 / 6"}}/>
            <Typography component="span" noWrap title={reason} sx={{gridRow: 2, gridColumn: "2 / 5", fontSize: 12, color: "text.secondary", minWidth: 0}}>
                {isNew && <Box component="span" sx={{fontWeight: 700, color: "text.primary", mr: 0.5}}>new</Box>}
                {reason}
            </Typography>
            <Money mean={earningMean(row)} revPrev={row.rev_prev} days={days} last={row.rev_last} y={row.yesterday} sx={{gridRow: 2, gridColumn: 5}}/>
            <Box sx={{gridRow: "1 / 3", gridColumn: 6, display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                <MiniBars daily={row.daily} width={barsWidth} height={18} gap={days > 14 ? 1 : 2} partialLast={row.partial_last_day}
                          label={`${row.tag}: daily revenue`}/>
            </Box>
        </Box>
    );
};

/** ≥ 3 tags of the same partner with the same problem since the same day: one line, tag list, summed bars; expands. */
export const GroupRow = ({group, days, open, onToggle, onSelect, selectedTag, isNew = false, launches = null}) => {
    const barsWidth = barsWidthFor(days);
    const tags = group.rows.map((r) => r.tag);
    const shown = tags.slice(0, GROUP_TAGS_SHOWN);
    const rest = tags.length - shown.length;
    const label = STATUS_META[group.status] ? STATUS_META[group.status].label : group.status;
    const showNetwork = Boolean(group.network) && !providerCoversNetwork(group.provider, group.network, group.prefix);
    return (
        <>
            <Box role="button" tabIndex={0} aria-expanded={open}
                 aria-label={`${tags.length} tags ${label}, ${shortProvider(group.provider)}, ${open ? "collapse" : "expand"}`}
                 onClick={onToggle} onKeyDown={keyActivate(onToggle)} sx={rowSx(barsWidth, {group: true})}>
                <Box sx={{gridRow: 1, gridColumn: 1, display: "flex", alignItems: "center"}}><SeverityDot status={group.status}/></Box>
                <Typography component="span" sx={{...TYPE.tag, fontSize: 11.5, gridRow: 1, gridColumn: 2, whiteSpace: "nowrap"}}>{tags.length} tags</Typography>
                <Box sx={{gridRow: 1, gridColumn: 3, display: "flex", alignItems: "center"}}><StatusChip status={group.status}/></Box>
                <Typography component="span" noWrap sx={{gridRow: 1, gridColumn: "4 / 6", fontSize: 12.5, minWidth: 0}}
                            title={`${shortProvider(group.provider)}${showNetwork ? ` · ${group.network}` : ""}`}>
                    <Box component="span" sx={{color: "text.primary", fontWeight: 500}}>{shortProvider(group.provider)}</Box>
                    {showNetwork && (
                        <><Box component="span" sx={{color: "text.disabled"}}> · </Box><Box component="span" sx={{color: "text.disabled"}}>{group.network}</Box></>
                    )}
                </Typography>
                <Typography component="span" noWrap sx={{gridRow: 2, gridColumn: "2 / 5", fontSize: 12, color: "text.secondary", minWidth: 0}}
                            title={`${group.reason}${group.note ? ` · ${group.note}` : ""} — ${tags.join(", ")}`}>
                    {isNew && <Box component="span" sx={{fontWeight: 700, color: "text.primary", mr: 0.5}}>new</Box>}
                    {group.launch ? `launch · day ${group.launch.day_n} · ` : ""}{group.reason}{group.note ? ` · ${group.note}` : ""}
                    <Box component="span" sx={{color: "text.disabled"}}> · </Box>
                    <Box component="span" sx={{fontFamily: MONO, fontSize: 11.5}}>{shown.join(" ")}{rest > 0 ? ` +${rest}` : ""}</Box>
                </Typography>
                <Money mean={group.mean} revPrev={group.revPrev} days={days} last={group.revLast} y={group.y} sx={{gridRow: 2, gridColumn: 5}}/>
                <Box sx={{gridRow: "1 / 3", gridColumn: 6, display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                    <MiniBars daily={group.daily} width={barsWidth} height={18} gap={days > 14 ? 1 : 2} partialLast={group.partialLast}
                              label={`${tags.length} tags: summed daily revenue`}/>
                </Box>
                <ExpandMoreIcon sx={{gridRow: "1 / 3", gridColumn: 7, fontSize: 16, color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s"}}/>
            </Box>
            {open && group.rows.map((r) => <AttentionRow key={r.tag} row={r} days={days} selected={selectedTag === r.tag} onSelect={onSelect} child launch={launches ? launches.get(r.tag) || null : null}/>)}
        </>
    );
};

/** Sum the daily revenue of `rows` day by day (for the group's bars). */
const sumDaily = (rows) => {
    const base = rows.find((r) => Array.isArray(r.daily) && r.daily.length);
    if (!base) return [];
    return base.daily.map((d, i) => ({day: d.day, rev: rows.reduce((s, r) => s + ((r.daily && r.daily[i] && Number(r.daily[i].rev)) || 0), 0)}));
};

/** Display items: single rows, or groups of ≥ 3 rows sharing partner + status + since-key; sorted by money at risk. */
export const buildAttentionItems = (attention) => {
    const by = new Map();
    attention.forEach((r) => {
        const k = `${r.provider || ""}|${r.status}|${sinceKey(r)}`;
        if (!by.has(k)) by.set(k, []);
        by.get(k).push(r);
    });
    const items = [];
    by.forEach((rs, key) => {
        if (rs.length < GROUP_MIN) { rs.forEach((r) => items.push({kind: "row", key: r.tag, row: r, atRisk: r._atRisk || 0, rev: r.rev || 0})); return; }
        const rows = [...rs].sort((a, b) => ((b._atRisk || 0) - (a._atRisk || 0)) || (b.rev - a.rev));
        const means = rows.map(usualOf).filter((m) => m !== null);   // the same "usually" the member rows print
        items.push({
            kind: "group", key, rows, status: rows[0].status, provider: rows[0].provider, network: rows[0].type_label, prefix: rows[0].prefix, reason: reasonOf(rows[0]),
            atRisk: rows.reduce((s, r) => s + (r._atRisk || 0), 0), rev: rows.reduce((s, r) => s + (r.rev || 0), 0),
            mean: means.length ? means.reduce((s, m) => s + m, 0) : null, revPrev: rows.reduce((s, r) => s + (r.rev_prev || 0), 0),
            revLast: rows.reduce((s, r) => s + (Number(r.rev_last) || 0), 0), daily: sumDaily(rows), partialLast: rows.some((r) => r.partial_last_day),
            note: pendingNote(rows[0]), y: null,
        });
    });
    return items.sort((a, b) => (b.atRisk - a.atRisk) || (b.rev - a.rev));
};

// ---------------------------------------------------------------------------------------------------------------------

/**
 * Needs attention: alarm rows (error + warning) sorted by money at risk, grouped when ≥ 3 share partner + problem + day;
 * count chips filter by status; "Also:" counts of the informational statuses. The movers live in `MovedPanel` (v2.3).
 */
/** `atRiskDay` = the API's figure (the same one the overview sentence quotes) while no filter is active; else the client sum. */
const AttentionPanel = ({attention, counts, statuses, onToggleStatus, onSelect, selectedTag, days, infoTags, atRiskDay = null}) => {
    const [expanded, setExpanded] = useState(false);
    const [openGroups, setOpenGroups] = useState({});
    const items = useMemo(() => buildAttentionItems(attention), [attention]);
    const shown = expanded ? items : items.slice(0, VISIBLE);
    const chips = CHIP_ORDER.filter((s) => counts[s]);
    const also = ALSO_ORDER.filter((s) => counts[s]);
    const total = CHIP_ORDER.reduce((n, s) => n + (counts[s] || 0), 0);
    const atRisk = atRiskDay !== null ? atRiskDay : attention.reduce((s, r) => s + (r._atRisk || 0), 0);
    const hiddenTags = items.slice(VISIBLE).reduce((n, it) => n + (it.kind === "group" ? it.rows.length : 1), 0);

    return (
        <Paper variant="outlined" sx={{borderRadius: 1.5, p: 1.5, pt: 1.25, display: "flex", flexDirection: "column", minWidth: 0}}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{columnGap: 1, rowGap: 0.5}}>
                <Typography component="h2" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em", color: total ? "#995400" : "text.secondary"}}>
                    Needs attention
                </Typography>
                <Typography component="span" sx={{fontSize: 13, fontWeight: 700, ...TABULAR, color: total ? "#995400" : "text.secondary"}}>{total}</Typography>
                {atRisk > 0 && (
                    <Typography component="span" sx={{fontSize: 12, color: "text.secondary", ...TABULAR, whiteSpace: "nowrap"}}>
                        · <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>{fmtMoneyShort(atRisk)}/d</Box> at risk
                    </Typography>
                )}
                {chips.map((s) => {
                    const sev = severityOf(s);
                    const active = statuses.includes(s);
                    return (
                        <Chip key={s} size="small" clickable onClick={() => onToggleStatus(s)} title={STATUS_META[s].hint}
                              label={`${counts[s]} ${STATUS_WORD[s]}`} aria-pressed={active}
                              icon={<SeverityDot status={s} size={7} sx={{ml: "6px !important"}}/>}
                              sx={{
                                  height: 22, fontSize: 11.5, fontWeight: 600, ...TABULAR,
                                  bgcolor: active ? (t) => SEV[sev].chipBg(t) : "transparent",
                                  border: "1px solid", borderColor: active ? (t) => SEV[sev].dot(t) : NEUTRAL.divider,
                                  color: active ? (t) => SEV[sev].chipText(t) : "text.secondary",
                                  "& .MuiChip-label": {pl: 0.5},
                              }}/>
                    );
                })}
                <Box sx={{flexGrow: 1}}/>
                <HelpPopover label="Needs attention" lines={ATTENTION_HELP}/>
            </Stack>
            <Typography component="div" noWrap title={STATUS_LEGEND} sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.35, mt: 0.5, mb: 0.75, ...TABULAR}}>{STATUS_LEGEND}</Typography>

            {attention.length === 0 ? (
                <Typography sx={{fontSize: 13, color: "text.secondary", py: 1.5, px: 1.5}}>Nothing is losing money right now.</Typography>
            ) : (
                <Box sx={{mx: -1.5, borderTop: `1px solid ${NEUTRAL.hairline}`}}>
                    {shown.map((it) => (it.kind === "group"
                        ? <GroupRow key={it.key} group={it} days={days} open={Boolean(openGroups[it.key])} selectedTag={selectedTag} onSelect={onSelect}
                                    onToggle={() => setOpenGroups((g) => ({...g, [it.key]: !g[it.key]}))}/>
                        : <AttentionRow key={it.key} row={it.row} days={days} selected={selectedTag === it.row.tag} onSelect={onSelect}/>
                    ))}
                </Box>
            )}
            {items.length > VISIBLE && (
                <Button size="small" onClick={() => setExpanded((e) => !e)} sx={{alignSelf: "flex-start", textTransform: "none", fontSize: 12, mt: 0.25, minHeight: 26, py: 0}}>
                    {expanded ? "Show fewer" : `Show ${hiddenTags} more`}
                </Button>
            )}

            {also.length > 0 && (
                <Typography component="div" sx={{...TYPE.caption, mt: 0.75, pt: 1, borderTop: `1px solid ${NEUTRAL.hairline}`, ...TABULAR}}>
                    Also:{" "}
                    {also.map((s, i) => {
                        const tags = infoTags[s] || [];
                        const label = `${counts[s]} ${STATUS_WORD[s]}`;
                        const detail = s === "PARTIAL" && tags.length ? ` (${tags.slice(0, 4).join(", ")}${tags.length > 4 ? ", …" : ""})` : "";
                        return (
                            <React.Fragment key={s}>
                                {i > 0 && " · "}
                                <Tooltip title={`${STATUS_META[s].hint}${tags.length ? ` — ${tags.slice(0, 20).join(", ")}${tags.length > 20 ? ", …" : ""}` : ""}`} placement="top" arrow>
                                    <Link component="button" type="button" underline="hover" onClick={() => onToggleStatus(s)}
                                          aria-pressed={statuses.includes(s)}
                                          sx={{fontSize: "inherit", fontFamily: "inherit", color: statuses.includes(s) ? "primary.main" : "text.secondary", fontWeight: statuses.includes(s) ? 700 : 400, verticalAlign: "baseline"}}>
                                        {label}
                                    </Link>
                                </Tooltip>
                                <Box component="span" sx={{fontFamily: MONO, fontSize: 11}}>{detail}</Box>
                            </React.Fragment>
                        );
                    })}
                </Typography>
            )}
            {attention.length === 0 && also.length === 0 && <Typography sx={TYPE.caption}>{EMPTY}</Typography>}
        </Paper>
    );
};

export default AttentionPanel;
